import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Building2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { showToastNotification } from "../../utility/helper";
import { Button } from "../../components/ui/button";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import TablePagination from "../../components/common/TablePagination";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import type { IFilter } from "@/components/common/CommonFilter";
import type { IPageInfo } from "@/interface/common.interface";
import AddDepartmentModal from "./AddDepartmentModal";
import CommonFilter from "@/components/common/CommonFilter";

interface IDepartment {
  id: string;
  name: string;
  description?: string;
  status: "active" | "inactive";
  createdAt?: string;
}

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const Department: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [departmentList, setDepartmentList] = useState<IDepartment[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] =
    useState<IDepartment | null>(null);

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    status: "",
  });

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  // Fetch departments (mock for now – replace with API)
  const fetchDepartments = async () => {
    setIsLoading(true);
    try {
      // TODO: Replace with actual API call
      const mockData: IDepartment[] = [
        {
          id: "d1",
          name: "Civil",
          description: "Civil works",
          status: "active",
        },
        {
          id: "d2",
          name: "Electrical",
          description: "Electrical installations",
          status: "active",
        },
        {
          id: "d3",
          name: "Plumbing",
          description: "Plumbing and sanitation",
          status: "inactive",
        },
      ];
      setDepartmentList(mockData);
      setPaginationData((prev) => ({
        ...prev,
        total: mockData.length,
        totalPages: 1,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const handleSearchChange = (value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleFilterChange = (value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const openAddModal = () => {
    setEditingDepartment(null);
    setShowModal(true);
  };

  const openEditModal = (department: IDepartment) => {
    setEditingDepartment(department);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingDepartment(null);
  };

  const handleModalSuccess = () => {
    fetchDepartments();
  };

  const columns: TableColumn<IDepartment>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-sm">
          {(paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1}
        </span>
      ),
    },
    {
      key: "name",
      header: "Department Name",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Building2 className="h-4 w-4" />
          </div>
          <span className="font-semibold text-gray-700">{item.name}</span>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (item) => (
        <span className="text-gray-600">{item.description || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${
            item.status === "active"
              ? "text-green-600 border-green-600 bg-green-50"
              : "text-red-600 border-red-600 bg-red-50"
          }`}
        >
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => openEditModal(item)}
              className="cursor-pointer"
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                // TODO: Delete API call
                showToastNotification("info", "Delete not implemented yet");
              }}
              className="cursor-pointer text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <MainHeading title="Department Management" />
          <Button onClick={openAddModal} className="gap-2 w-full sm:w-auto">
            <Plus size={16} /> Add Department
          </Button>
        </div>

        <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
          <CommonFilter
            searchValue={filter.search || ""}
            onSearchChange={handleSearchChange}
            value={filter}
            filterOptions={STATUS_OPTIONS}
            filterName="status"
            filterPlaceholder="Select Status"
            onChange={handleFilterChange}
          />
          <CommonTable
            data={departmentList}
            columns={columns}
            loading={isLoading}
            stickyColumns={2}
          />
          <TablePagination
            paginationData={paginationData}
            setPaginationData={setPaginationData}
            handleChangePageSize={handleChangePageSize}
            isLoading={isLoading}
          />
        </div>
      </div>

      <AddDepartmentModal
        show={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        editData={editingDepartment}
      />
    </div>
  );
};

export default Department;
